const { cELM, getWindowHeight, getAnswerVariableCssClass } = require("../utils");
const Scroller = require("../components/scroller");
const Block = require("../components/blocks/index");
const ProgressBar = require("../components/progress-bar");
const ScrollNotification = require("../components/scroll-notification");
const CollaboratingProviders = require("../components/collaboratingProviders");
const moment = require('moment');

class SchedulerModule {
  constructor(type, value, parentRef) {
    this.type = type;
    this.value = value;
    this.parent = parentRef;
    const cssVariableKey = getAnswerVariableCssClass(value);
    this.elContainer = cELM("div", `scheduler-module ${cssVariableKey}`)
    this.contentHash = {};
    this.typingIndicator = null;
    const now = new Date();
    this.navInfo = {
      today: new Date(`${now.getMonth()+1}/${now.getDate()}/${now.getFullYear()}`),
      navFullDate: new Date(),
      navDay: now.getDate(),
      navMonth: now.getMonth(),
      navYear: now.getFullYear(),
      navEndDate: null,
      maxNavigatedMonthYear: new Date(now.getFullYear(), now.getMonth()),
      prevScreen: 1,
      screen: 1,
      yearFromToday: new Date(`${now.getMonth()+1}/${now.getDate()}/${now.getFullYear()+1}`),
      nextAvailableDate: null
    };
    this.fetchedTimeSlots = null;
    this.loadingMoreSlots = {
      bool: false
    };
    this.dateSelectedBool = {
      bool: false
    };
    this.invalidTimeSlot = null;
    this.daySelected = null;
    this.monthSelected = null;
    this.yearSelected = null;
    this.noSlotsReturned = false;
    this.initialScroll = false;
    this.hideSlots = {}; // AB: For situations where patients try to select slots that are no longer available
  }

  handle(type, value, reference) {
    if (value && value.format) {
      if (value.format.includes("scheduler-cancel-appointment")) {
        this.elContainer.classList.add('scheduler-cancel-appointment');
      } else if (value.format.includes("scheduler-book-appointment")) {
        this.elContainer.classList.add('scheduler-book-appointment');
      }
    };

    if (value.values.isAnswer) {
      this.parent.showTyping();
    }
    else if (value.values.isError) {
      const question = this.contentHash[value.values.id][1];
      question.updateAnswer(value.values, this.clearAfterId.bind(this));
    }
    else if (this.typingIndicator) {
      this.elContainer.removeELM(this.typingIndicator);
      this.typingIndicator = null;
    }

    if (value.progress) {
      ProgressBar.updateProgress(value.progress);
    }

    const _elm = this.handleOnMessage(value);
    if (!_elm) return;
    this.elContainer.addELM(_elm);

    if (value.values.format === "schedule-date") {
      if (!this.initialScroll) {
        if (value.status !== "typing") {
          Scroller.scrollToElm(_elm, () => {
            _elm.focus();
          });
        }
        this.initialScroll = true;
      }
    } else if (value.status !== "typing") {
      Scroller.scrollToElm(_elm, () => {
        _elm.focus();
      });
    }
  }

  findEarlistDateInTimeslots = (timeSlots) => {
    const earliestSlot = Object.values(timeSlots).reduce((previousSlot, currentSlot) =>
      moment(previousSlot.dateTime, 'MM/DD/YYYY HH:mm').isAfter(moment(currentSlot.dateTime, 'MM/DD/YYYY HH:mm')) ? currentSlot: previousSlot
    );
    return moment(earliestSlot.dateTime, "MM/DD/YYYY HH:mm");
  }

  filterAndSortSlotsByProvider = (qualifiedProviders, fetchedTimeSlots, primaryProviderId) => {
    const openSlots = Object.entries(fetchedTimeSlots);
    let providerSlots = Array.isArray(qualifiedProviders) ? [...qualifiedProviders] : [];
    providerSlots.forEach(prov => {
      return prov.timeSlots = Object.fromEntries(openSlots.filter(([key, slot]) => {
        // Check if either providerid or localproviderid matches the provider's ID
        const slotProviderId = slot.providerid || slot.localproviderid;
        return slotProviderId == prov.providerId;
      }));
    });

    if (providerSlots.length > 1) {
        // This means we have more than one collaborating provider
        // We need to sort these providers by soonest availability
        providerSlots = providerSlots.sort((a, b) => {
          const aTimeslots = Object.keys(a.timeSlots);
          const bTimeslots = Object.keys(b.timeSlots);

          // If one provider has slots and the other doesn't, the provider with slots should come first
          if (!aTimeslots.length || !bTimeslots.length) {
            return bTimeslots.length - aTimeslots.length;
          }

          // Both non-empty, compare earliest date
          const earliestA = this.findEarlistDateInTimeslots(a.timeSlots);
          const earliestB = this.findEarlistDateInTimeslots(b.timeSlots);
          return earliestA - earliestB;
        });
    }

    const primaryProviderSlots = Object.fromEntries(openSlots.filter(([key, slot]) => {
      // Check if either providerid or localproviderid matches the primary provider's ID
      const slotProviderId = slot.providerid || slot.localproviderid;
      return slotProviderId == primaryProviderId;
    }));

    // Primary provider should always come first
    providerSlots.unshift({
      providerId: primaryProviderId,
      timeSlots: primaryProviderSlots
    });

    return providerSlots;
  }

  handleOnMessage(value) {
    let component = null;

    if (value.values.invalidTimeSlot) {
      let splitChosenDate = value.values.dateChosenUnformatted.split('/');
      this.invalidTimeSlot = true;
      this.daySelected = Number(splitChosenDate[1]);
      value.values.daySelected = this.daySelected;
      this.monthSelected = splitChosenDate[0]-1;
      value.values.monthSelected = this.monthSelected;
      this.yearSelected = Number(splitChosenDate[2]);
      value.values.yearSelected = this.yearSelected;
    }
    if (value.format === "empty") {
      return null;
    };

    if ((
      (!(value.values.fetchedTimeSlots || []).length && !this.loadingMoreSlots.bool)
      || (this.loadingMoreSlots.bool && !value.values.additionalTimeSlots)
    ) && !(value.values.dateChosenFormatted || value.values.dateChosenUnformatted)) {
      this.noSlotsReturned = true;
    } else {
      this.noSlotsReturned = false;
    };

    const customTexts = value.values.customTexts;
    if (customTexts && customTexts.emptySlots && this.noSlotsReturned) {
      component = new Block({
        data: {
          text: customTexts.emptySlots.text,
          subtext: customTexts.emptySlots.subtext
        }
      });

      return component;
    }

    this.loadingMoreSlots.bool = false;

    if (!this.fetchedTimeSlots) {
      this.fetchedTimeSlots = {};
    }

    if ((value.values.additionalTimeSlots || []).length) {
      for (const timeSlot of value.values.additionalTimeSlots) {
        if (!timeSlot) {
          continue;
        }
        this.fetchedTimeSlots[timeSlot.appointmentid] = timeSlot;
      }
    }
    if ((value.values.fetchedTimeSlots || []).length) {
      for (const timeSlot of value.values.fetchedTimeSlots) {
        if (!timeSlot) {
          continue;
        }
        this.fetchedTimeSlots[timeSlot.appointmentid] = timeSlot;
      };
    };

    // AB: Include some state from parent in calendar component, not deep copy, mutable from within component
    value.values.fetchedTimeSlots = this.fetchedTimeSlots;
    value.values.navInfo = this.navInfo;
    value.values.dateSelectedBool = this.dateSelectedBool;
    value.values.loadingMoreSlots = this.loadingMoreSlots;
    value.values.hideSlots = this.hideSlots;
    value.values.specificSlotsPerProvider = this.filterAndSortSlotsByProvider(value.values.qualifiedProviders, this.fetchedTimeSlots, value.values.primaryProviderId);

    value.values.noSlotsReturned = this.noSlotsReturned;

    let identifier = value.values.id;

    if (identifier.indexOf("/answer") > -1) {
      const idParts = identifier.split("/answer/");
      identifier = idParts[0] + "/" + idParts[1];
      const question = this.contentHash[identifier][1];
      const isAnswerNo = value.values.text && value.values.text.trim && value.values.text.trim().toLowerCase() === 'no';
      if (value.values.format === 'yes/back' && isAnswerNo) {
        if (this.contentHash[identifier]) {
          this.elContainer.removeELM(this.contentHash[identifier][1]);
          delete this.contentHash[identifier];
        }
      } else {
        question.updateAnswer(value.values, this.clearAfterId.bind(this));
      }
    } else if (identifier.indexOf("/suggestions") > -1) {
      component = this.createComponent(value);
      identsplit = (identifier || "").split("suggestions/");
      identifier = identsplit[0] + identsplit[1];
      component = (this.contentHash[identifier] || [])[1] || component;
      if (
        value.continue === false &&
        component._option._element.updateSuggestions
      ) {
        this.typingIndicator = null;
        component._option._element.updateSuggestions(value.values, { sendObject: true });
        return;
      }
    } else if (!this.contentHash[identifier]) {
      component = this.createComponent(value)

      if (value.status === "typing" || value.id.indexOf("/") === -1) {
        this.typingIndicator = component;
      } else {
        this.contentHash[identifier] = [
          Object.keys(this.contentHash).length,
          component
        ];
      }
    } else if (this.contentHash[identifier]) {
      if (value.values.suggestions) {
        if (this.contentHash[identifier][1]._option._element && this.contentHash[identifier][1]._option._element.updateSuggestions) {
          this.contentHash[identifier][1]._option._element.updateSuggestions(value.values, { sendObject: true });
        }
      } else {
        let ch = this.contentHash[identifier];
        this.elContainer.removeELM(ch[1]);

        component = this.createComponent(value)

        this.contentHash[identifier] = [
          Object.keys(this.contentHash).length,
          component
        ];
      }
    }

    return component;
  }

  createComponent(value) {
    if (value.values.answerKey === 'scheduler-datetimechosen' &&
      value.values.format === 'schedule-date' &&
      value.values.qualifiedProviders?.length > 0) {
        // This will only be true if config.enableCollaboratingProviders is true
        return new CollaboratingProviders({ data: value.values });
    }

    return new Block({
      data: value.values
    });
  }

  getHTML() {
    return this.elContainer;
  }

  getELM() {
    return this.elContainer;
  }

  clearAfterId(id) {
    const index = this.contentHash[id][0];
    const newHash = {};

    Object.keys(this.contentHash).forEach(key => {
      const ch = this.contentHash[key];
      if (ch[0] >= index) {
        this.elContainer.removeELM(ch[1]);
      } else {
        newHash[key] = ch;
      }
    });

    this.contentHash = newHash;

    if(this.parent && this.parent.contentHash && this.parent.contentHash[this.value.id]) {
      const currentIndex = this.parent.contentHash[this.value.id][0];
      Object.keys(this.parent.contentHash).forEach(pKey => {
        const ch = this.parent.contentHash[pKey];
        if(ch[0] > currentIndex) {
          delete this.parent.contentHash[pKey];
          this.parent.elContainer.removeELM(ch[1]);
        }
      });
    }

    if(Object.keys(this.contentHash).length <= 0) {
      this.parent &&
        this.parent.clearAfterId &&
        this.parent.clearAfterId(this.value.id);
    }
  }

  showMore(elm) {
    if (elm) {
      const height = getWindowHeight();
      const bound = elm.getBoundingClientRect();

      if (bound.height > height) {
        ScrollNotification.attachElement(elm);
      }
    }
  }
}

module.exports = SchedulerModule;
